import { API_BASE_URL, apiClient } from './httpClient';
import api from './api';

/**
 * Coach Hearen Service
 * Handles all coach-related API calls
 * 
 * ⭐ V3 iOS FIX: All API calls now use apiClient instead of direct fetch
 * This ensures correct API_BASE_URL resolution on iOS Capacitor
 */

// Helper to get auth token
const getToken = () => localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

export const coachService = {
  /**
   * Get weekly plan
   * @param {string} week - Optional week in format YYYY-Www
   * @returns {Promise<Object>} Plan with goals, sessions, score
   */
  async getPlan(week = null) {
    try {
      const url = week ? `/api/coach/plan?week=${week}` : `/api/coach/plan`;
      const token = getToken();
      
      console.log('[CoachService] getPlan - API_BASE_URL:', API_BASE_URL);
      
      const response = await apiClient.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      // Handle 403 status for BETA_DISABLED
      if (error.response?.status === 403) {
        const data = error.response?.data;
        if (data?.detail === 'beta_coach_disabled') {
          throw new Error('BETA_DISABLED');
        }
        throw new Error(data?.detail || `HTTP 403`);
      }
      console.error('Error getting coach plan:', error);
      throw error;
    }
  },

  /**
   * Recalculate plan for current week
   * @returns {Promise<Object>} Updated state
   */
  async recalculatePlan() {
    try {
      const token = getToken();
      const response = await apiClient.post(`/api/coach/plan/recalculate`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error recalculating plan:', error);
      throw error;
    }
  },

  /**
   * Get summary for specified range
   * @param {string} range - Range in format "28d"
   * @returns {Promise<Object>} Summary with metrics
   */
  async getSummary(range = '28d') {
    try {
      const token = getToken();
      const response = await apiClient.get(`/api/coach/summary?range=${range}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error getting summary:', error);
      throw error;
    }
  },

  /**
   * Post event (session_complete, session_skip, etc.)
   * @param {Object} event - Event object with type, ts, meta
   * @returns {Promise<Object>} Updated progress
   */
  async postEvent(event) {
    try {
      const token = getToken();
      const response = await apiClient.post(`/api/coach/event`, event, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error posting event:', error);
      throw error;
    }
  },

  /**
   * Get messages for today
   * @returns {Promise<Array>} Array of messages
   */
  async getMessagesToday() {
    try {
      const token = getToken();
      const response = await apiClient.get(`/api/coach/messages/today`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = response.data;
      return data.messages || [];
    } catch (error) {
      console.error('Error getting messages:', error);
      throw error;
    }
  },

  /**
   * Acknowledge messages as seen
   * @param {Array<string>} messageIds - Array of message IDs
   * @returns {Promise<Object>} Confirmation
   */
  async ackMessages(messageIds) {
    try {
      const token = getToken();
      const response = await apiClient.post(`/api/coach/ack`, { message_ids: messageIds }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error acknowledging messages:', error);
      throw error;
    }
  },
  
  /**
   * Get today's check-in status
   * @returns {Promise<Object>} Check-in data
   */
  async getTodayCheckin() {
    try {
      const token = getToken();
      const response = await apiClient.get(`/api/coach/checkin/today`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('[CoachService] Error loading today checkin:', error);
      throw error;
    }
  },
  
  /**
   * Submit daily check-in
   * @param {number} impactScore - Impact score (0-10)
   * @returns {Promise<Object>} Confirmation
   */
  async submitCheckin(impactScore) {
    try {
      const token = getToken();
      const response = await apiClient.post(`/api/coach/checkin`, { impact_score: impactScore }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('[CoachService] Error submitting checkin:', error);
      throw error;
    }
  }
};

export default coachService;
